import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { computed } from '@ember/object';
import { all, task } from "ember-concurrency";
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { CLASSIFICATION_TYPES } from 'sh-www/models/classification';
import LightboxOverlay from 'sh-www/components/modals/overlays/lightbox';
import { showModal } from 'sh-www/utils/modal-controls';
import { isUrl } from 'sh-www/utils/misc';
import { getClassificationIdsFromObject, mapClassificationsToCategories } from 'sh-www/helpers/admin/map-classifications-to-categories';
import { ADMIN_CONTENT_SEARCH } from 'sh-www/constants/routes/admin/content-search';

const { PROJECT_DIGEST_INCLUDES } = ADMIN_CONTENT_SEARCH;

class ClassificationController extends Controller {
  @service('commands/projectdigest-commands') projectDigestCommandService;
  @service('commands/classification-commands') classificationCommandService;
  @service('state-managers/application') applicationStateManager;
  @service('video-thumbnail-from-url') videoThumbnailService;
  @service ('error-tracker') errorTracker;
  @service moment;
  @service shData;

  @tracked newFeatured = false;
  @tracked newClassifications = {};

  @computed('loadClassificationsTask.lastSuccessful.value')
  get classificationItems() {
    return mapClassificationsToCategories(this.loadClassificationsTask.lastSuccessful?.value);
  }

  @computed('loadProjectDigestToClassifyTask.last.value')
  get projectdigestToClassify() {
    return this.loadProjectDigestToClassifyTask.last?.value;
  }

  @task
  *loadProjectDigestToClassifyTask() {
    try {
      const response = yield this.projectDigestCommandService.getProjectdigestToClassify(PROJECT_DIGEST_INCLUDES);
      this.newFeatured = response.featured;
      this.newClassifications = mapClassificationsToCategories(response.classifications);
      return response;
    } catch (error) {
      this.errorTracker.error('loadProjectDigestToClassifyTask - failed to fetch projectdigest items for classification', this, error);
      this.applicationStateManager.addToast("Failed to fetch a project to classify. Please try again.");
    }
  }

  @task
  *loadClassificationsTask() {
    try {
      return yield this.classificationCommandService.loadClassifications({
        filters: {
          types: [
            CLASSIFICATION_TYPES.TYPE_DEFAULT,
            CLASSIFICATION_TYPES.TYPE_ADMIN_ONLY,
          ],
        },
      });
    } catch (error) {
      this.errorTracker.error('loadClassificationsTask - failed to fetch classifications', this, error);
      this.applicationStateManager.addToast("Failed to load classifications. Please try again.");
    }
  }

  @task
  *updateProjectdigestTask(classificationIds, featured) {
    try {
      const pending = [this.projectDigestCommandService.updateProjectdigestClassifications(this.projectdigestToClassify.id, classificationIds)];

      // Only updated featured value if changed
      if (this.projectdigestToClassify.featured !== featured) {
        pending.push(this.projectDigestCommandService.updateProjectdigestFeaturedStatus(this.projectdigestToClassify.id, featured));
      }

      yield all(pending);
      return yield all([this.loadProjectDigestToClassifyTask.perform(), this.loadClassificationsTask.perform()]);
    } catch (error) {
      this.errorTracker.error('updateProjectdigestTask - failed to update classifications', this, error);
      this.applicationStateManager.addToast("Failed to update classifications. Please try again.");
    }
  }

  @action
  setFeaturedChanged(newValue) {
    this.newFeatured = newValue;
  }

  @action
  setClassificationsChanged(newValues) {
    this.newClassifications = newValues;
  }

  @action
  handleViewNextClicked() {
    const classificationIds = getClassificationIdsFromObject(this.newClassifications);
    this.updateProjectdigestTask.perform(classificationIds, this.newFeatured);
  }

  @action
  handleAddToast(message) {
    this.applicationStateManager.addToast(message);
  }

  @action
  handleGetThumbnail(link) {
    return isUrl(link) ? this.videoThumbnailService.getThumb(link) : null;
  }

  @action
  handleOpenVideoPlayer(projectDigest) {
    showModal(
      this,
      LightboxOverlay,
      {
        digestsToShowForVideoCarousel: [projectDigest],
      }
    );
  }
}

export default ClassificationController;
